/**
 * Non-secret pricing snapshot used for prerendering and first paint.
 *
 * Keep these public fields aligned with
 * bottradesforyou/packages/server/src/billing/pricing-config.ts. The browser
 * refreshes them from the public `billing.pricingTiers` query after hydration.
 * Crypto checkout has no provider product IDs at all — prices are ours.
 */
export type PlanSlug = "starter" | "pro" | "enterprise" | "quant";

export interface PublicPricingTier {
  slug: PlanSlug;
  name: string;
  description: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  isPopular: boolean;
  displayOrder: number;
  limits: {
    /** Total bot slots; every slot may run when fleet capacity is healthy. */
    bots: number;
    maxLiveBots: number;
    maxPaperBots: number;
    fleetTier: "shared" | "dedicated";
    maxCpuUnits: number;
    maxRamMb: number;
    reservedEgressIp: boolean;
    /**
     * Agentic research runs per UTC day. Mirrors
     * `PricingTierLimits.agenticAnalysesPerDay`; the parity test in
     * `tests/unit/agentic/marketing.test.tsx` reads the server config and
     * fails if these drift.
     */
    agenticAnalysesPerDay: number;
    agenticAutomationRunsPerDay: null;
  };
  capabilities: {
    /** Current commercial availability, false until the launch gate. */
    agenticBotExecution: boolean;
    agenticBotLive: boolean;
  };
  purchasable: boolean;
}

export const PUBLIC_PRICING_SNAPSHOT: PublicPricingTier[] = [
  {
    slug: "starter",
    name: "Starter",
    description: "Run live and paper strategies on the shared fleet",
    monthlyPriceCents: 1900,
    annualPriceCents: 19000,
    isPopular: true,
    displayOrder: 0,
    limits: {
      bots: 5,
      maxLiveBots: 2,
      maxPaperBots: 3,
      fleetTier: "shared",
      maxCpuUnits: 2.5,
      maxRamMb: 2_560,
      reservedEgressIp: false,
      agenticAnalysesPerDay: 3,
      agenticAutomationRunsPerDay: null,
    },
    capabilities: { agenticBotExecution: false, agenticBotLive: false },
    purchasable: true,
  },
  {
    slug: "pro",
    name: "Pro",
    description: "More bots, alerts, and advanced analytics",
    monthlyPriceCents: 2900,
    annualPriceCents: 29000,
    isPopular: false,
    displayOrder: 1,
    limits: {
      bots: 15,
      maxLiveBots: 5,
      maxPaperBots: 10,
      fleetTier: "shared",
      maxCpuUnits: 7.5,
      maxRamMb: 7_680,
      reservedEgressIp: false,
      agenticAnalysesPerDay: 10,
      agenticAutomationRunsPerDay: null,
    },
    capabilities: { agenticBotExecution: false, agenticBotLive: false },
    purchasable: true,
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    description: "Dedicated fleet and reserved egress for larger portfolios",
    monthlyPriceCents: 19900,
    annualPriceCents: 199000,
    isPopular: false,
    displayOrder: 2,
    limits: {
      bots: 75,
      maxLiveBots: 25,
      maxPaperBots: 50,
      fleetTier: "dedicated",
      maxCpuUnits: 37.5,
      maxRamMb: 38_400,
      reservedEgressIp: true,
      agenticAnalysesPerDay: 50,
      agenticAutomationRunsPerDay: null,
    },
    capabilities: { agenticBotExecution: false, agenticBotLive: false },
    purchasable: true,
  },
  {
    slug: "quant",
    name: "Quant Desk",
    description: "Account-reserved fleet capacity for quantitative desks",
    monthlyPriceCents: 100_000,
    annualPriceCents: 1_000_000,
    isPopular: false,
    displayOrder: 3,
    limits: {
      bots: 200,
      maxLiveBots: 100,
      maxPaperBots: 100,
      fleetTier: "dedicated",
      maxCpuUnits: 100,
      maxRamMb: 102_400,
      reservedEgressIp: true,
      agenticAnalysesPerDay: 250,
      agenticAutomationRunsPerDay: null,
    },
    capabilities: { agenticBotExecution: false, agenticBotLive: false },
    purchasable: true,
  },
];
